import { useMemo, useState } from 'react';

import { useCapability } from '../../../../hooks/use-capability';
import { usePresence } from '../../../../hooks/use-presence';
import { useAuthStore } from '../../../../stores/auth-store';
import { PageHead } from '../../components';
import {
  useAcceptInsight,
  useArchiveInsight,
  useFalsePositiveInsight,
  useGenerationJobPoll,
  useSetSensitivity,
  useTakeInsightAction,
  useTriggerGeneration,
} from '../../hooks/use-insight-actions';
import { useInsightMetrics, useInsights, useInsightsHistory } from '../../hooks/use-insights';
import { FilterChip } from '../inbox-time/FilterChip';

import { InsightCard } from './InsightCard';
import { FalsePositiveModal } from './modals/FalsePositiveModal';
import { TakeActionModal } from './modals/TakeActionModal';
import { INSIGHT_CATEGORY_META, adaptInsightCard } from './tela10-adapter';

import type { InsightCardVM } from './tela10-adapter';
import type { InsightCategory, InsightLens, SensitivityMode } from '../../types';
import type { SupervisorVariant } from '../../view-models';
import type React from 'react';

const CATEGORY_OPTS: { value: InsightCategory; label: string }[] = (
  Object.keys(INSIGHT_CATEGORY_META) as InsightCategory[]
).map((c) => ({ value: c, label: INSIGHT_CATEGORY_META[c].label }));

const SENSITIVITY_OPTS: { value: SensitivityMode; label: string }[] = [
  { value: 'conservative', label: 'Conservador' },
  { value: 'balanced', label: 'Equilibrado' },
  { value: 'aggressive', label: 'Agressivo' },
];

function mapTier(pkg: string | undefined): SupervisorVariant {
  if (pkg === 'solo') return 'solo';
  if (pkg === 'regulated') return 'regulated';
  return 'team-pro';
}

function SoloEmpty(): React.JSX.Element {
  return (
    <>
      <PageHead title="Insights Estratégicos" />
      <div style={{ padding: 'var(--space-10)', textAlign: 'center', color: 'var(--fg-3)' }}>
        <div style={{ fontSize: 48 }}>🤖</div>
        <h3 style={{ color: 'var(--fg-1)' }}>Insights estratégicos requerem Pro+</h3>
        <p>É necessário ter uma equipe para detectar padrões de operação.</p>
      </div>
    </>
  );
}

type ModalKind = 'false-positive' | 'action';

/**
 * Tela de Insights Estratégicos. Compartilhada entre Supervisor (Tela 10, `lens='supervisor'`)
 * e Gestor (Tela 12, `lens='gestor'` — o follow-up D13). A lente parametriza os hooks de leitura
 * e a geração manual; o cache do react-query é isolado por lente (queryKey).
 */
export function InsightsPage({
  lens = 'supervisor',
}: { lens?: InsightLens } = {}): React.JSX.Element {
  const variant = mapTier(useAuthStore((s) => s.tenant?.packageType));
  const canRead = useCapability('ai-insight-supervisor');
  const canAction = useCapability('ai-insight-action');
  const canGenerate = useCapability('ai-insight-manual-trigger');

  // D12 (estendido): `period` NÃO é wired no backend (o service `list`/`history`/`aggregate-metrics`
  // ignora o campo do DTO). Expor um filtro de Período seria affordance falsa → omitido. Item 334.
  const [category, setCategory] = useState<string | undefined>(undefined);
  const [active, setActive] = useState<{ vm: InsightCardVM; kind: ModalKind } | null>(null);
  const [outcome, setOutcome] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const [jobId, setJobId] = useState<string | null>(null);
  const [sensitivity, setSensitivity] = useState<SensitivityMode | null>(null);

  const categoryFilter = category as InsightCategory | undefined;
  const pending = useInsights('pending', {
    lens,
    ...(categoryFilter !== undefined ? { category: categoryFilter } : {}),
  });
  const history = useInsightsHistory({
    lens,
    ...(categoryFilter !== undefined ? { category: categoryFilter } : {}),
  });
  const { metrics } = useInsightMetrics(undefined, lens);
  const presence = usePresence();

  const mAccept = useAcceptInsight();
  const mFalsePositive = useFalsePositiveInsight();
  const mAction = useTakeInsightAction();
  const mArchive = useArchiveInsight();
  const mTrigger = useTriggerGeneration(lens);
  const mSensitivity = useSetSensitivity();
  const { job, isDone } = useGenerationJobPoll(jobId);

  const nameFor = useMemo(() => {
    const list = presence.data as unknown as { userId: string; name?: string }[];
    return (userId: string): string | null => list.find((p) => p.userId === userId)?.name ?? null;
  }, [presence.data]);

  if (variant === 'solo' || !canRead) return <SoloEmpty />;

  const pendingCards = pending.rows.map(adaptInsightCard);
  // "Aceitos" = status accepted (sem janela temporal — aggregate-metrics e history não filtram por
  // data no backend; rotular "hoje" seria falso). `totalAccepted` é a contagem agregada real.
  const acceptedCards = history.rows.filter((r) => r.status === 'accepted').map(adaptInsightCard);
  const acceptedCount = metrics?.totalAccepted ?? acceptedCards.length;

  const loadFailed = pending.isError || history.isError;
  const isEmptyFirstUse =
    !pending.isLoading &&
    !history.isLoading &&
    pendingCards.length === 0 &&
    history.rows.length === 0 &&
    category === undefined;

  const anyPending =
    mAccept.isPending || mFalsePositive.isPending || mAction.isPending || mArchive.isPending;

  const closeModal = (): void => setActive(null);
  const openModal = (vm: InsightCardVM, kind: ModalKind): void => {
    setOutcome(null);
    setFailed(false);
    setActive({ vm, kind });
  };
  const onFail = (): void => setFailed(true);

  // Guard anti duplo-submit: ignora cliques enquanto qualquer mutation está em voo.
  const doAccept = (vm: InsightCardVM): void => {
    if (anyPending) return;
    mAccept.mutate({ id: vm.id }, { onError: onFail });
  };
  const doArchive = (vm: InsightCardVM): void => {
    if (anyPending) return;
    mArchive.mutate({ id: vm.id }, { onError: onFail });
  };
  const confirmFalsePositive = (args: {
    reason: Parameters<typeof mFalsePositive.mutate>[0]['reason'];
    feedbackText?: string;
  }): void => {
    if (active === null) return;
    mFalsePositive.mutate(
      { id: active.vm.id, ...args },
      { onSuccess: closeModal, onError: onFail },
    );
  };
  const confirmAction = (args: {
    actionType: Parameters<typeof mAction.mutate>[0]['actionType'];
    payload: Record<string, unknown>;
  }): void => {
    if (active === null) return;
    mAction.mutate(
      { id: active.vm.id, ...args },
      {
        onSuccess: (res) => {
          setOutcome(
            res.linkedResourceId === null
              ? 'Ação registrada (sem efeito funcional nesta versão).'
              : args.actionType === 'coach_schedule'
                ? 'Coaching agendado — atendente notificado.'
                : 'Ação executada — recurso criado.',
          );
        },
        onError: onFail,
      },
    );
  };

  const triggerGeneration = (): void => {
    mTrigger.mutate(undefined, {
      onSuccess: (res) => setJobId(res.jobId),
    });
  };
  const pickSensitivity = (mode: SensitivityMode): void => {
    setSensitivity(mode);
    mSensitivity.mutate({ mode });
  };

  return (
    <>
      <PageHead
        title="Insights Estratégicos"
        subtitle={
          isEmptyFirstUse
            ? 'Sistema aprendendo padrões da equipe'
            : `${pendingCards.length} pendentes · ${acceptedCount} aceitos`
        }
        actions={
          canGenerate ? (
            <button
              type="button"
              className="btn-ghost-mini btn-ghost-mini--primary"
              onClick={triggerGeneration}
              disabled={mTrigger.isPending || (jobId !== null && !isDone)}
            >
              {jobId !== null && !isDone ? 'Gerando…' : 'Gerar agora'}
            </button>
          ) : undefined
        }
        filters={
          <FilterChip
            label="Categoria"
            options={CATEGORY_OPTS}
            value={category}
            onChange={setCategory}
          />
        }
      />

      {loadFailed && (
        <div className="banner banner--danger" role="alert">
          <div className="banner__body">
            <span className="banner__title">Não foi possível carregar os insights.</span>
            <div className="banner__sub">Tente recarregar a página.</div>
          </div>
        </div>
      )}

      {jobId !== null && (
        <div className="banner banner--info" role="status">
          <span className="banner__icon" aria-hidden="true">
            {isDone ? '✓' : '⏳'}
          </span>
          <div className="banner__body">
            <span className="banner__title">
              {job?.status === 'failed'
                ? 'A geração falhou.'
                : isDone
                  ? `Geração concluída · ${job?.insightsGenerated ?? 0} novos · ${job?.duplicatesSuppressed ?? 0} duplicados suprimidos`
                  : 'Gerando insights…'}
            </span>
          </div>
        </div>
      )}

      {isEmptyFirstUse && !loadFailed && (
        <div style={{ padding: 'var(--space-10)', textAlign: 'center', color: 'var(--fg-3)' }}>
          <div style={{ fontSize: 48 }}>🤖</div>
          <h3 style={{ color: 'var(--fg-1)' }}>Sistema ainda aprendendo padrões da sua equipe</h3>
          <p>
            Insights aparecem conforme a equipe usa o Lume (tipicamente em ~48h de uso inicial).
          </p>
        </div>
      )}

      {!isEmptyFirstUse && (
        <div className="tela10-body">
          <div className="sla-section-title" style={{ marginBottom: 0 }}>
            Insights pendentes ({pendingCards.length} não tratados)
          </div>
          {pendingCards.length === 0 && (
            <div style={{ color: 'var(--fg-3)', padding: 'var(--space-3)' }}>
              Nenhum insight pendente para os filtros atuais.
            </div>
          )}
          <div className="tela10-body__col">
            {pendingCards.map((vm) => (
              <InsightCard
                key={vm.id}
                vm={vm}
                targetName={vm.targetUserId !== null ? nameFor(vm.targetUserId) : null}
                onAccept={doAccept}
                onFalsePositive={(v) => openModal(v, 'false-positive')}
                onArchive={doArchive}
              />
            ))}
          </div>

          {acceptedCards.length > 0 && (
            <>
              <div className="sla-section-title" style={{ marginTop: 'var(--space-4)' }}>
                Insights aceitos ({acceptedCards.length})
              </div>
              <div className="tela10-body__col">
                {acceptedCards.map((vm) => (
                  <InsightCard
                    key={vm.id}
                    vm={vm}
                    targetName={vm.targetUserId !== null ? nameFor(vm.targetUserId) : null}
                    canAction={canAction}
                    onAction={(v) => openModal(v, 'action')}
                    onArchive={doArchive}
                  />
                ))}
              </div>
            </>
          )}

          {/* Sensibilidade do agente (D10) — canto discreto de configuração. */}
          <div
            className="tela10-sensitivity"
            style={{
              marginTop: 'var(--space-4)',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <span className="tweaks-hint">Sensibilidade do agente:</span>
            {SENSITIVITY_OPTS.map((o) => (
              <button
                key={o.value}
                type="button"
                className={`chip ${sensitivity === o.value ? 'chip--active' : ''}`}
                disabled={mSensitivity.isPending}
                onClick={() => pickSensitivity(o.value)}
              >
                {o.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {active?.kind === 'false-positive' && (
        <FalsePositiveModal
          open
          isPending={anyPending}
          failed={failed}
          outcome={outcome}
          onClose={closeModal}
          onConfirm={confirmFalsePositive}
        />
      )}
      {active?.kind === 'action' && (
        <TakeActionModal
          open
          isPending={anyPending}
          failed={failed}
          outcome={outcome}
          targetUserId={active.vm.targetUserId}
          targetName={active.vm.targetUserId !== null ? nameFor(active.vm.targetUserId) : null}
          onClose={closeModal}
          onConfirm={confirmAction}
        />
      )}
    </>
  );
}
